import type { FilterDefConfig, FilterPreferences, GenericFilterModel } from '@tractian/ui-filters'
import { Filters } from '@tractian/ui-filters'
import { createContext, useContext, useState, type ReactNode } from 'react'

import { filtersAdapter, filtersLocale } from '@/components/toolkit-filters/filters-config'
import { useQueryParamsFilters } from '@/components/toolkit-filters/hooks/use-query-params-filters'

type FiltersContextType<T extends GenericFilterModel = GenericFilterModel> = {
  filterValues: T
  setFilterValues: (values: T) => void
  appliedFilterCount: number
  hasFiltersApplied: boolean
}

const FiltersContext = createContext<FiltersContextType | undefined>(undefined)

// Order-independent structural equality for filter models (primitives + arrays of primitives).
function filterValuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => filterValuesEqual(v, b[i]))
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a)
    const bk = Object.keys(b)
    return (
      ak.length === bk.length &&
      ak.every((k) =>
        filterValuesEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
      )
    )
  }
  return false
}

type FiltersProviderProps<T extends GenericFilterModel> = {
  name: string
  filterDefs: FilterDefConfig<T>[]
  defaultValues?: Partial<T>
  useUrlValues?: boolean
  // Keys dropped from the persisted preferences and the URL on load (e.g. a
  // filter that no longer exists but may linger in old links or saved state).
  ignoredKeys?: (keyof T)[]
  onFiltersChange?: (values: T) => void
  children: ReactNode
}

export function FiltersProvider<T extends GenericFilterModel>({
  name,
  filterDefs,
  defaultValues = {} as Partial<T>,
  useUrlValues = false,
  ignoredKeys,
  onFiltersChange,
  children,
}: FiltersProviderProps<T>) {
  const [urlFilters, setUrlFilters] = useQueryParamsFilters<T>()

  const [filterValues, setFilterValuesState] = useState<T>(() => {
    const preferences = filtersAdapter.getPreferences?.(name, filterDefs as unknown as FilterDefConfig<GenericFilterModel>[]) as FilterPreferences<T> | undefined
    const initial = {
      ...(defaultValues as T),
      ...preferences?.filterValues,
      ...(useUrlValues && urlFilters ? urlFilters : {}),
    } as T
    for (const key of ignoredKeys ?? []) delete initial[key]
    return initial
  })

  function setFilterValues(values: T) {
    // `@tractian/ui-filters` emits an onChange in a post-mount effect carrying the
    // already-persisted filter values. Since the initial state is seeded from those same
    // preferences, that emit is redundant — but acting on it flips the query key
    // (default → persisted) and fires a second list request, flashing a second skeleton.
    // Ignore no-op changes so hydration stays a single fetch.
    if (filterValuesEqual(values, filterValues)) return

    setFilterValuesState(values)
    onFiltersChange?.(values)

    const existing = filtersAdapter.getPreferences?.(name, filterDefs as unknown as FilterDefConfig<GenericFilterModel>[]) as FilterPreferences<T> | undefined
    filtersAdapter.setPreferences?.(name, { ...existing, filterValues: values } as FilterPreferences)

    if (useUrlValues) {
      setUrlFilters(values)
    }
  }

  const appliedFilterCount = Object.values(filterValues).filter((v) => {
    if (v === null || v === undefined) return false
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'string') return v.trim() !== ''
    return true
  }).length

  return (
    <FiltersContext.Provider
      value={
        {
          filterValues,
          setFilterValues,
          appliedFilterCount,
          hasFiltersApplied: appliedFilterCount > 0,
        } as unknown as FiltersContextType
      }
    >
      <Filters.ConfigProvider adapter={filtersAdapter} locale={filtersLocale}>
        <Filters
          name={name}
          filterDefs={filterDefs as unknown as FilterDefConfig<GenericFilterModel>[]}
          value={filterValues}
          onChange={(values) => setFilterValues(values as T)}
        >
          {children}
        </Filters>
      </Filters.ConfigProvider>
    </FiltersContext.Provider>
  )
}

export function useFiltersContext<T extends GenericFilterModel>() {
  const context = useContext(FiltersContext) as FiltersContextType<T> | undefined
  if (!context) {
    return {
      filterValues: {} as T,
      setFilterValues: () => undefined,
      appliedFilterCount: 0,
      hasFiltersApplied: false,
    }
  }
  return context
}
